const MAP_SOURCE_ID = 'econtainers-basemap'

const MAP_NAME_PROPERTIES = {
  pl: ['name:pl', 'name', 'name:en', 'name_en'],
  en: ['name:en', 'name', 'name:pl', 'name_en'],
  de: ['name:de', 'name:en', 'name', 'name:pl', 'name_en']
}

const getLocalizedName = locale => {
  const nameProperties = MAP_NAME_PROPERTIES[locale] || MAP_NAME_PROPERTIES.en

  return [
    'coalesce',
    ...nameProperties.map(nameProperty => ['get', nameProperty])
  ]
}

const roadWidth = [
  'interpolate',
  ['linear'],
  ['zoom'],
  5,
  0.45,
  9,
  1.1,
  14,
  4.5
]

const roadCasingWidth = [
  'interpolate',
  ['linear'],
  ['zoom'],
  5,
  1.7,
  9,
  2.35,
  14,
  5.75
]

const isPmtilesSource = sourceUrl =>
  /^pmtiles:\/\//i.test(sourceUrl) || /\.pmtiles(?:$|\?)/i.test(sourceUrl)

const getMapSource = sourceUrl => {
  if (!isPmtilesSource(sourceUrl)) {
    return {
      type: 'vector',
      url: sourceUrl
    }
  }

  return {
    type: 'vector',
    url: /^pmtiles:\/\//i.test(sourceUrl)
      ? sourceUrl
      : `pmtiles://${sourceUrl}`,
    attribution:
      '<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap</a> · <a href="https://www.openmaptiles.org/" target="_blank">© OpenMapTiles</a>'
  }
}

const createMapStyleConfig = (sourceUrl, localizedName) => ({
  version: 8,
  glyphs:
    process.env.GATSBY_MAP_GLYPHS_URL ||
    'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
  sources: {
    [MAP_SOURCE_ID]: getMapSource(sourceUrl)
  },
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#f5f3ef' }
    },
    {
      id: 'landcover',
      type: 'fill',
      source: MAP_SOURCE_ID,
      'source-layer': 'landcover',
      paint: {
        'fill-color': [
          'match',
          ['get', 'class'],
          ['wood', 'forest'],
          '#dcebd7',
          ['grass', 'grassland'],
          '#e8f0d8',
          ['farmland'],
          '#eee9d5',
          '#ecebe4'
        ],
        'fill-opacity': 0.72
      }
    },
    {
      id: 'landuse',
      type: 'fill',
      source: MAP_SOURCE_ID,
      'source-layer': 'landuse',
      paint: {
        'fill-color': [
          'match',
          ['get', 'class'],
          'residential',
          '#e9e7e3',
          'industrial',
          '#e4e1df',
          'cemetery',
          '#dce9d7',
          '#ebe9e4'
        ],
        'fill-opacity': 0.72
      }
    },
    {
      id: 'parks',
      type: 'fill',
      source: MAP_SOURCE_ID,
      'source-layer': 'park',
      paint: {
        'fill-color': '#d7ead2',
        'fill-opacity': 0.76
      }
    },
    {
      id: 'water',
      type: 'fill',
      source: MAP_SOURCE_ID,
      'source-layer': 'water',
      paint: { 'fill-color': '#b9dce8' }
    },
    {
      id: 'waterways',
      type: 'line',
      source: MAP_SOURCE_ID,
      'source-layer': 'waterway',
      minzoom: 7,
      paint: {
        'line-color': '#a5cfdf',
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.5, 14, 2]
      }
    },
    {
      id: 'boundaries',
      type: 'line',
      source: MAP_SOURCE_ID,
      'source-layer': 'boundary',
      paint: {
        'line-color': '#8d97a0',
        'line-dasharray': [3, 2],
        'line-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          3,
          0.85,
          10,
          0.42
        ],
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.7, 10, 1.4]
      }
    },
    {
      id: 'road-casing',
      type: 'line',
      source: MAP_SOURCE_ID,
      'source-layer': 'transportation',
      minzoom: 5,
      filter: ['!=', ['get', 'class'], 'path'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#d1d4d5',
        'line-width': roadCasingWidth,
        'line-opacity': 0.78
      }
    },
    {
      id: 'roads',
      type: 'line',
      source: MAP_SOURCE_ID,
      'source-layer': 'transportation',
      minzoom: 5,
      filter: ['!=', ['get', 'class'], 'path'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'class'],
          ['motorway', 'trunk'],
          '#f4c886',
          ['primary', 'secondary'],
          '#fff6df',
          '#ffffff'
        ],
        'line-width': roadWidth,
        'line-opacity': 0.96
      }
    },
    {
      id: 'buildings',
      type: 'fill',
      source: MAP_SOURCE_ID,
      'source-layer': 'building',
      minzoom: 13,
      paint: {
        'fill-color': '#d8d4d0',
        'fill-outline-color': '#c8c3bd'
      }
    },
    {
      id: 'road-labels',
      type: 'symbol',
      source: MAP_SOURCE_ID,
      'source-layer': 'transportation_name',
      minzoom: 9,
      layout: {
        'symbol-placement': 'line',
        'text-field': localizedName,
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-letter-spacing': 0.01,
        'text-max-angle': 30
      },
      paint: {
        'text-color': '#667078',
        'text-halo-color': 'rgba(255, 255, 255, 0.9)',
        'text-halo-width': 1.25
      }
    },
    {
      id: 'place-labels',
      type: 'symbol',
      source: MAP_SOURCE_ID,
      'source-layer': 'place',
      minzoom: 3,
      layout: {
        'text-field': localizedName,
        'text-font': ['Open Sans Regular'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4,
          ['match', ['get', 'class'], 'city', 13, 10],
          10,
          ['match', ['get', 'class'], 'city', 17, 'town', 14, 12]
        ],
        'text-padding': 8,
        'text-max-width': 9,
        'text-letter-spacing': 0.01,
        'symbol-sort-key': ['coalesce', ['get', 'rank'], 20]
      },
      paint: {
        'text-color': '#3f4850',
        'text-halo-color': 'rgba(255, 255, 255, 0.92)',
        'text-halo-width': 1.4
      }
    }
  ]
})

export const createMapStyle = (sourceUrl, locale) =>
  createMapStyleConfig(sourceUrl, getLocalizedName(locale))
